/**
 * NumericInputUI - 基于 Bootstrap 的数值输入控制组件
 * 支持键盘上下键修改数值功能
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface NumericInputOptions extends ElementOptions {
  /** 最小值 */
  min?: number | null;
  /** 最大值 */
  max?: number | null;
  /** 步长 */
  step?: number;
  /** 精度（小数位数） */
  precision?: number;
  /** 初始值 */
  value?: number;
  /** 标签文本 */
  label?: string;
  /** 是否显示标签 */
  showLabel?: boolean;
  /** 是否显示当前值 */
  showValue?: boolean;
  /** 值显示格式 */
  valueFormat?: (value: number) => string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否只读 */
  readonly?: boolean;
  /** 是否允许空值 */
  allowNull?: boolean;
  /** 标签样式类 */
  labelClass?: string;
  /** 控制容器样式类 */
  controlsContainerClass?: string;
  /** 输入框样式类 */
  inputClass?: string;
  /** 值显示区域的样式类 */
  valueClass?: string;
  /** 容器样式类 */
  containerClass?: string[];
  /** 是否在输入时触发变化事件 */
  keyChange?: boolean;
  /** 是否在按Enter时失焦 */
  blurOnEnter?: boolean;
  /** 是否在按Escape时失焦 */
  blurOnEscape?: boolean;
  /** 占位符文本 */
  placeholder?: string;
  /** 标签是否与输入框同行显示 */
  isLabelInline?: boolean;
}

export class NumericInputUI {
  private ui: BootstrapUI;
  private container: HTMLElement;
  private inputElement: HTMLInputElement;
  private valueDisplayElement: HTMLElement | null = null;
  private options: NumericInputOptions;
  private onChangeCallbacks: ((value: number | null) => void)[] = [];

  // 防止循环更新的标志
  private isUpdating: boolean = false;
  private previousValue: number = 0;

  constructor(options: NumericInputOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      min: null,
      max: null,
      step: 0.1,
      precision: 2,
      value: 0,
      label: '数值输入控制',
      showLabel: true,
      showValue: true,
      valueFormat: (value: number) => value.toFixed(2),
      disabled: false,
      readonly: false,
      allowNull: false,
      labelClass: '',
      controlsContainerClass:'',
      inputClass: '',
      valueClass: '',
      containerClass: [],
      keyChange: false,
      blurOnEnter: true,
      blurOnEscape: true,
      isLabelInline: false,
      ...options
    };

    this.container = this.createElement();
    this.inputElement = this.container.querySelector('input[type="number"]') as HTMLInputElement;
    this.valueDisplayElement = this.container.querySelector('.numeric-input-value') as HTMLElement;

    this.setupEventHandlers();
  }

  /**
   * 创建数值输入组件元素
   * @returns 包含输入框的容器元素
   */
  private createElement(): HTMLElement {
    let labeli18nKey = '';
    if(this.options.i18nKey){
      labeli18nKey = this.options.i18nKey;
      this.options.i18nKey = '';
    }
    const container = this.ui.createElement({
      className: [
        'numeric-inputui-container',
        ...(this.options.containerClass ? this.options.containerClass : []),
        ...(this.options.isLabelInline ? [] : ['flex-direction-column']),
      ],
      ...this.options
    });

    // 创建标签
    if (this.options.label && this.options.showLabel) {
      const label = this.ui.createElement({
        tag: 'label',
        className: ['form-label',
          ...(this.options.labelClass ? [this.options.labelClass] : []),
        ],
        textContent: this.options.label,
        attributes: {
          for: this.options.id || ''
        },
        i18nKey: labeli18nKey
      });
      container.appendChild(label);
    }

    // 创建输入框容器
    const controlsContainer = this.ui.createElement({
      className: [
        'numeric-input-controls',
        ...(this.options.controlsContainerClass ? [this.options.controlsContainerClass] : []),
      ],
    });

    const inputContainer = this.ui.createElement({
      className: 'numeric-input-container',
    });

    const input = this.ui.createElement({
      tag: 'input',
      className: [
        'form-control',
        ...(this.options.inputClass ? [this.options.inputClass] : []),
      ],
      attributes: {
        type: 'number',
        ...(this.options.min !== null && this.options.min !== undefined && { min: this.options.min.toString() }),
        ...(this.options.max !== null && this.options.max !== undefined && { max: this.options.max.toString() }),
        step: this.options.step?.toString() || '0.1',
        value: this.options.value?.toString() || '0',
        placeholder: this.options.placeholder || '',
        ...(this.options.disabled && { disabled: 'true' }),
        ...(this.options.readonly && { readonly: 'true' }),
        ...(this.options.id && { id: this.options.id })
      },
    }) as HTMLInputElement;

    inputContainer.appendChild(input);
    controlsContainer.appendChild(inputContainer);
    container.appendChild(controlsContainer);

    // 创建值显示区域
    if (this.options.showValue) {
      const valueContainer = this.ui.createElement({
        className: [
          'numeric-input-value',
          ...(this.options.valueClass ? [this.options.valueClass] : []),
        ],
      });

      const valueLabel = this.ui.createElement({
        tag: 'span',
        className: 'value-label',
        textContent: '当前值: '
      });

      const valueDisplay = this.ui.createElement({
        tag: 'span',
        className: 'value-display',
        textContent: this.options.valueFormat ?
          this.options.valueFormat(this.options.value || 0) :
          (this.options.value || 0).toString()
      });

      valueContainer.appendChild(valueLabel);
      valueContainer.appendChild(valueDisplay);
      container.appendChild(valueContainer);
    }

    return container;
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    // 输入框变化事件 - input事件会使输入框中的值一直变化，所以使用change事件
    this.ui.on('change', this.inputElement, (e) => {
      if (this.isUpdating) return;
      const target = e.target as HTMLInputElement;
      const value = this.normalizeValue(target.value);
      this.syncFromInput(value);
    });

    // 键盘事件
    this.ui.on('keydown', this.inputElement, (e) => {
      this.handleKeyDown(e as KeyboardEvent);
    });

    this.ui.on('keyup', this.inputElement, (e) => {
      if (this.options.keyChange) {
        this.handleKeyUp(e as KeyboardEvent);
      }
    });

    // 焦点事件
    this.ui.on('focus', this.inputElement, (e) => {
      const currentValue = this.getValue();
      this.previousValue = currentValue === null ? 0 : currentValue;
    });

    // 鼠标滚轮事件
    this.ui.on('wheel', this.inputElement, (e) => {
      this.handleWheel(e as WheelEvent);
    });
  }

  /**
   * 处理键盘按下事件
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (this.options.disabled || this.options.readonly) return;

    // 上下箭头键调整数值
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const direction = e.key === 'ArrowUp' ? 1 : -1;
      const step = e.shiftKey ? (this.options.step || 0.1) * 0.1 : (this.options.step || 0.1);
      const currentValue = this.getValue();
      const newValue = (currentValue === null ? 0 : currentValue) + step * direction;
      this.syncFromInput(newValue);
    }
    // Enter键失焦
    else if (e.key === 'Enter' && this.options.blurOnEnter) {
      this.inputElement.blur();
    }
    // Escape键恢复原值并失焦
    else if (e.key === 'Escape') {
      this.syncFromInput(this.previousValue);
      if (this.options.blurOnEscape) {
        this.inputElement.blur();
      }
    }
  }

  /**
   * 处理键盘抬起事件
   */
  private handleKeyUp(e: KeyboardEvent): void {
    if (e.key !== 'Escape') {
      const value = this.normalizeValue(this.inputElement.value);
      this.syncFromInput(value);
    }
  }

  /**
   * 处理鼠标滚轮事件
   */
  private handleWheel(e: WheelEvent): void {
    if (this.options.disabled || this.options.readonly) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // deltaY > 0 表示向下滚动（减少值），deltaY < 0 表示向上滚动（增加值）
    const direction = e.deltaY < 0 ? 1 : -1;
    
    // Shift 键按住时使用更小的步长
    const step = e.shiftKey ? (this.options.step || 0.1) * 0.1 : (this.options.step || 0.1);
    
    const currentValue = this.getValue();
    const newValue = (currentValue === null ? 0 : currentValue) + step * direction;
    this.syncFromInput(newValue);
  }

  /**
   * 从输入框同步
   */
  public syncFromInput(value: number | null): void {
    this.isUpdating = true;
    const normalizedValue = this.normalizeValue(value);
    this.inputElement.value = normalizedValue === null ? '' : normalizedValue.toString();
    this.updateValueDisplay(normalizedValue);
    this.notifyChange(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 标准化数值
   */
  private normalizeValue(value: any): number | null {
    try {
      if (typeof value === 'string') {
        // 检查空值
        if (value === '' || value === null || value === undefined) {
          return this.options.allowNull ? null : 0;
        }

        // 替换逗号为点号（国际化键盘支持）
        value = value.replace(/,/g, '.');

        // 移除空格
        value = value.replace(/\s/g, '');

        // 解析数值
        value = parseFloat(value);
      }

      if (isNaN(value)) {
        return this.options.allowNull ? null : 0;
      }

      // 限制在最小最大值范围内
      const { min, max } = this.options;

      if (typeof min === 'number') {
        value = Math.max(min, value);
      }

      if (typeof max === 'number') {
        value = Math.min(max, value);
      }

      // 应用精度
      if (this.options.precision !== null && this.options.precision !== undefined) {
        if (this.options.precision < 0) {
          value = parseFloat(value);
        } else {
          value = parseFloat(value.toFixed(this.options.precision));
        }
      }

      return value;
    } catch (error) {
      return this.options.allowNull ? null : 0;
    }
  }

  /**
   * 更新值显示
   */
  private updateValueDisplay(value: number | null): void {
    if (this.valueDisplayElement) {
      if (value === null) {
        this.valueDisplayElement.textContent = 'null';
      } else if (this.options.valueFormat) {
        this.valueDisplayElement.textContent = this.options.valueFormat(value);
      } else {
        this.valueDisplayElement.textContent = value.toString();
      }
    }
  }

  /**
   * 通知值变化
   */
  private notifyChange(value: number | null): void {
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(value);
      } catch (error) {
        console.error('数值输入变化回调执行错误:', error);
      }
    });
  }

  /**
   * 获取当前值
   */
  getValue(): number | null {
    return this.normalizeValue(this.inputElement.value);
  }

  /**
   * 设置当前值
   */
  setValue(value: number | null): void {
    const normalizedValue = this.normalizeValue(value);
    this.isUpdating = true;
    this.inputElement.value = normalizedValue === null ? '' : normalizedValue.toString();
    this.updateValueDisplay(normalizedValue);
    this.isUpdating = false;
  }

  /**
   * 获取最小值
   */
  getMin(): number | null {
    return this.options.min ?? null;
  }

  /**
   * 设置最小值
   */
  setMin(min: number | null): void {
    this.options.min = min ?? null;
    this.inputElement.min = min !== null && min !== undefined ? min.toString() : '';
  }

  /**
   * 获取最大值
   */
  getMax(): number | null {
    return this.options.max ?? null;
  }

  /**
   * 设置最大值
   */
  setMax(max: number | null): void {
    this.options.max = max ?? null;
    this.inputElement.max = max !== null && max !== undefined ? max.toString() : '';
  }

  /**
   * 获取步长
   */
  getStep(): number {
    return this.options.step || 0.1;
  }

  /**
   * 设置步长
   */
  setStep(step: number): void {
    this.options.step = step;
    this.inputElement.step = step.toString();
  }

  /**
   * 获取精度
   */
  getPrecision(): number {
    return this.options.precision || 2;
  }

  /**
   * 设置精度
   */
  setPrecision(precision: number): void {
    this.options.precision = precision;
  }

  /**
   * 是否禁用
   */
  isDisabled(): boolean {
    return this.inputElement.disabled;
  }

  /**
   * 设置禁用状态
   */
  setDisabled(disabled: boolean): void {
    this.inputElement.disabled = disabled;
    this.options.disabled = disabled;
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    if (!status) {
      // 禁用整个组件
      this.container.classList.add('forbidden-event-dom');
      
      // 禁用输入框
      this.setDisabled(true);
    } else {
      // 启用整个组件
      this.container.classList.remove('forbidden-event-dom');
      
      // 启用输入框
      this.setDisabled(false);
    }
  }

  /**
   * 是否只读
   */
  isReadOnly(): boolean {
    return this.inputElement.readOnly;
  }

  /**
   * 设置只读状态
   */
  setReadOnly(readonly: boolean): void {
    this.inputElement.readOnly = readonly;
    this.options.readonly = readonly;
  }

  /**
   * 添加值变化监听器
   */
  onChange(callback: (value: number | null) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除值变化监听器
   */
  offChange(callback: (value: number | null) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取输入框元素
   */
  getInputElement(): HTMLInputElement {
    return this.inputElement;
  }

  /**
   * 获取容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 聚焦输入框
   */
  focus(): void {
    this.inputElement.focus();
  }

  /**
   * 失焦输入框
   */
  blur(): void {
    this.inputElement.blur();
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];

    // 移除事件监听器
    this.ui.off('input', this.inputElement, () => { });
    this.ui.off('change', this.inputElement, () => { });
    this.ui.off('keydown', this.inputElement, () => { });
    this.ui.off('keyup', this.inputElement, () => { });
    this.ui.off('focus', this.inputElement, () => { });
    this.ui.off('wheel', this.inputElement, () => { });

    // 移除DOM元素
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// 导出默认实例创建函数
export function createNumericInputUI(options: NumericInputOptions = {}): NumericInputUI {
  return new NumericInputUI(options);
}
